import React from "react";
import { useFormState } from "react-final-form";
import { Box, Typography } from "@mui/material";
import {
  CheckboxFormField,
  RadioFormField,
} from "../../common/components/FormFields";
import { IlingnunUkpiginFormResult } from "../ilingnunUkpigin";

export interface IlingnunUkpiginConsentProps {}

export function IlingnunUkpiginConsent({}: IlingnunUkpiginConsentProps) {
  const { values } = useFormState<IlingnunUkpiginFormResult>();

  return (
    <Box>
      <Typography variant="h5" paragraph>
        PARTICIPANT CONSENT
      </Typography>
      <Box pb={2}>
        <CheckboxFormField
          name="participantConsent"
          mustBeTrue
          label="I confirm I have read and understand this form and by clicking this box, I consent to the collection, use and disclosure of my personal and health information as described above. *"
        />
      </Box>

      <Typography variant="h5" paragraph>
        PHOTO AND VIDEO CONSENT
      </Typography>
      <Typography paragraph>
        Photos and videos may be taken during Ilingnun Ukpigin events. These may
        be used by IRC for reports, promotional purposes and may be shared
        online through the IRC website and accompanying social media platforms.
      </Typography>
      <RadioFormField
        name="photoVideoConsent"
        options={["Yes", "No"]}
        required
        label="Do you give IRC permission to use photos and videos of you for these purposes?"
      />

      <Box mt={4}>
        <Typography variant="h5" paragraph>
          PARTICIPATION, ASSUMPTION OF RISK, RELEASE OF LIABILITY AND INDEMNITY
          AGREEMENT (the “Agreement”)
        </Typography>
        <Typography fontStyle="italic" fontWeight="bold" paragraph>
          Please read the following Agreement carefully, since you will be
          waiving certain legal rights.
        </Typography>
        <Typography fontWeight="bold" paragraph>
          To: Inuvialuit Regional Corporation (IRC) and its directors, officers,
          employees, agents, guides, volunteers, independent contractors,
          representatives, successors and assigns.
        </Typography>
        <Typography paragraph>
          <b>
            I, {values.firstName ?? ""} {values.lastName ?? ""}
          </b>
          , have applied to voluntarily participate in the IRC ILINGNUN UKPIGIN
          (the “Program”), which involves various on-the-land excursions in the
          wilderness. I understand that this Agreement applies to all
          activities, events or services provided, organized, conducted or
          authorized by IRC in relation to the Program.
        </Typography>
        <Typography paragraph>
          1. <b>ACKNOWLEDGEMENT OF RISK:</b> I am aware and understand that
          there are inherent risks, dangers and hazards associated with
          participating in the Program including but not limited to personal
          injury, death, damage or loss of property and negligence on the part
          of IRC.
        </Typography>
        <Typography paragraph>
          2. <b>ASSUMPTION OF RISK:</b> I FREELY AND VOLUNTARILY ACCEPT AND
          ASSUME ALL SUCH RISKS, whether known or unknown, foreseen or
          unforeseen, AND ASSUME ALL RESPONSIBILITY for my participation in the
          Program, including risks arising from the negligence of IRC to the
          fullest extent permitted by law.
        </Typography>
        <Typography paragraph>
          3. <b>RELEASE OF LIABILITY, WAIVER OF CLAIMS AND INDEMNITY:</b> In
          consideration of participating in the Program (which I have agreed to
          do voluntarily), and for good and valuable consideration, the receipt
          of which is hereby acknowledged, I agree to the following:
        </Typography>
        <Box pl={3}>
          <Typography paragraph>
            i. TO WAIVE ANY AND ALL CLAIMS that I have or may in the future have
            against IRC AND TO RELEASE IRC from any and all liability for any
            loss, damage, expense or injury, including death, that I may suffer
            or that my next of kin may suffer while participating in the Program
            DUE TO ANY CAUSE WHATSOEVER, INCLUDING NEGLIGENCE, BREACH OF
            CONTRACT, OR BREACH OF ANY STATUTORY OR OTHER DUTY OF CARE ON THE
            PART OF IRC.
          </Typography>
          <Typography paragraph>
            ii. I UNDERSTAND THAT NEGLIGENCE INCLUDES FAILURE ON THE PART OF IRC
            TO TAKE REASONABLE STEPS TO SAFEGUARD OR PROTECT ME FROM ANY AND ALL
            RISKS, DANGERS, AND HAZARDS WHICH MAY ARISE WHILE PARTICIPATING IN
            THE PROGRAM.
          </Typography>
          <Typography paragraph>
            iii. TO HOLD HARMLESS AND INDEMNIFY IRC from any liability,
            INCLUDING NEGLIGENCE, for any property damage or personal injury to
            any third party resulting from my participation in the Program.
          </Typography>
        </Box>
        <Typography paragraph>
          4. <b>NO TRAVEL OR HEALTH INSURANCE PROVIDED:</b> I understand that
          IRC has not purchased any health, travel or other insurance on my
          behalf and obtaining such coverage is my sole responsibility.
        </Typography>
        <Typography paragraph>
          5. <b>ZERO TOLERANCE:</b> I understand that the Program is a
          zero-tolerance program and if I bring or use alcohol or drugs (other
          than cigarettes or medication prescribed to me by a medical doctor) I
          will be immediately removed from the Program.
        </Typography>
        <Typography paragraph>
          6. <b>RESPECT:</b> I will demonstrate respect to facilitators,
          participants, the environment, cabins and all activities forming part
          of the Program. I will follow the direction of the facilitators and
          understand that if a facilitator perceives a risk to any Program
          participant or other facilitator, he or she is authorized to remove me
          from the Program. I will not use any machinery located at the cabins
          without the permission and supervision of the owner.
        </Typography>
        <Typography paragraph>
          7. <b>RETURN TO TOWN:</b> I understand that during the Program we will
          not be travelling to town except in the event of emergency or to
          replenish necessary supplies.
        </Typography>
        <Typography paragraph>
          8. <b>GENERAL PROVISIONS</b>
        </Typography>
        <Box pl={3}>
          <Typography paragraph>
            i. This Agreement shall be effective and binding upon my heirs, next
            of kin, executors, administrators, assigns and representatives in
            the event of my death or incapacity.
          </Typography>
          <Typography paragraph>
            ii. This Agreement is governed by and interpreted solely in
            accordance with the laws of the Northwest Territories and no other
            jurisdiction and no litigation involving the parties to this
            Agreement shall be brought except within the Northwest Territories
            and shall be within the exclusive jurisdiction of the Supreme Court
            of the Northwest Territories.
          </Typography>
          <Typography paragraph>
            iii. If any provision of this Agreement is found to be invalid,
            illegal or unenforceable, the remaining provisions shall remain in
            full force and effect. The parties agree to replace any invalid
            provision with a valid provision that most closely reflects the
            original intent.
          </Typography>
          <Typography paragraph>
            iv. Nothing in this Agreement shall be construed as an admission of
            liability or wrongdoing by IRC.
          </Typography>
        </Box>
        <Box pb={2}>
          <CheckboxFormField
            name="agreementConsent"
            mustBeTrue
            label="I CONFIRM THAT I HAVE READ AND UNDERSTAND THIS AGREEMENT, THAT I AM WAIVING CERTAIN LEGAL RIGHTS ON BEHALF OF MYSELF, AND THAT BY CHECKING THIS BOX I HEREBY AGREE TO BE BOUND TO THE TERMS AND CONDITIONS AND ACCEPT ALL RISKS AND LIABILITIES AS NOTED ABOVE. *"
          />
        </Box>
      </Box>
    </Box>
  );
}
